import { Divider, Radio, Stack, Tooltip, Typography } from '@mui/material';
import { type SecurityScanResult, type Snapshot } from '@repo/api-gw-sdk';
import classnames from 'classnames';

import {
  getEvidenceStrings,
  hasSecurityIssue,
} from '@/components/security/securityUtils';
import { SkullIcon } from '@/components/security/skullIcon';
import { Icon } from '@/components/shared/icon';
import { SnapshotIndicator } from '@/components/snapshots/SnapshotIndicator';
import { LockIcon } from '@/components/snapshots/lockIcon';
import VaultTag from '@/components/vaults/vaultTag';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useUser } from '@/contexts/useUser';
import { PreferencesKey } from '@/data/dal/preferencesKey';
import type { BackupVault } from '@/data/vaults/backupVault';
import { isDateValid } from '@/utils/dateTime';
import { dayjs, TIME_FORMAT } from '@/utils/dayjs';

interface SnapshotDetailsHeaderProps {
  snap: Snapshot;
  vault: BackupVault | undefined;
  isSelected: boolean;
  onSelect: () => void;
  snapshotSelection: boolean;
}

export const SnapshotDetailsHeader = ({
  snap,
  isSelected,
  onSelect,
  vault,
  snapshotSelection,
}: SnapshotDetailsHeaderProps) => {
  const dal = useDAL();
  const { isEmployee } = useUser();
  const { isDev, isDemo } = useEnvironment();

  const { data: prefResponse } = dal.preferences.getUserPref(
    PreferencesKey.Preferences
  );

  const showSnapshotId =
    isDev || (isEmployee && !isDemo && prefResponse?.value?.isDevMode);

  const date = isDateValid(snap.pointInTime)
    ? snap.pointInTime
    : snap.createdTime;

  const showPartial = snap.accessDeniedItemsCount || snap.skippedItemsCount;

  return (
    <Stack
      className={classnames('flex-1', { 'cursor-pointer': snapshotSelection })}
      direction='row'
      gap='40px'
      alignItems='center'
      onClick={() => onSelect()}
    >
      <Stack direction='row' gap='12px' alignItems='center'>
        {snapshotSelection && <Radio size='small' checked={isSelected} />}
        {vault && (
          <SnapshotIndicator
            sx={{
              margin: '6px',
            }}
            backgroundColor={vault.backgroundColor || null}
            isPartial={!!snap.accessDeniedItemsCount}
            isPitr={!!snap.supportPiTR}
          />
        )}
        <Stack>
          <Typography width='100px' variant='subtitle1'>
            {`${dayjs.utc(date).format(TIME_FORMAT)} UTC`}
          </Typography>
          {showSnapshotId && <Typography>{snap.id}</Typography>}
        </Stack>

        <Stack
          width='102px'
          direction={'row'}
          gap='12px'
          alignItems='center'
          divider={<Divider orientation='vertical' flexItem />}
        >
          {showPartial && <PartialInfoIndication snap={snap} />}
          {hasSecurityIssue(snap) && <RansomwareIndication snap={snap} />}
          {snap.protectedFromDeletion && <ProtectedFromDeletionIndication />}
        </Stack>
      </Stack>
      {vault && (
        <Stack direction='row' gap='12px' alignItems='center'>
          <Typography variant='subtitle1'>Stored in</Typography>
          <VaultTag vault={vault} />
        </Stack>
      )}
    </Stack>
  );
};

const PartialInfoIndication = ({ snap }: { snap: Snapshot }) => {
  let title = '';
  if (snap.accessDeniedItemsCount) {
    title =
      snap.accessDeniedItemsCount === 1
        ? 'This is a partial snapshot. 1 file wasn`t backed up.'
        : `This is a partial snapshot. ${snap.accessDeniedItemsCount} files weren’t backed up.`;
  } else if (snap.skippedItemsCount) {
    title = `This Point-in-time includes ${snap.skippedItemsCount} fewer logical databases than the previous full snapshot. This may happen if databases were deleted or if there was an issue during backup.`;
  }

  return (
    <Tooltip
      title={title}
      slotProps={{
        tooltip: {
          sx: {
            padding: '16px',
          },
        },
      }}
    >
      <Icon iconClass='material-symbols-info-outline' />
    </Tooltip>
  );
};

const RansomwareIndication = ({ snap }: { snap: Snapshot }) => {
  const evidences: SecurityScanResult[] = snap.securityScan?.results || [];
  const description = `This snapshot is infected with ransomware.${evidences.length ? ' Suspicious indicators found:' : ''}`;

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: '240px',
          },
        },
      }}
      title={
        <Stack padding='4px' gap='12px'>
          <Typography color='var(--mui-palette-Tooltip-text)'>
            {description}
          </Typography>
          <Stack gap='4px'>
            {evidences.map((evidence) => (
              <Stack
                key={evidence.evidenceType}
                direction='row'
                justifyContent='space-between'
              >
                <Typography color='var(--mui-palette-Tooltip-text)'>
                  {getEvidenceStrings(evidence.evidenceType).label}
                </Typography>
                <Typography color='var(--mui-palette-Tooltip-text)'>
                  {evidence.count}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      }
    >
      <span>
        <SkullIcon />
      </span>
    </Tooltip>
  );
};

const ProtectedFromDeletionIndication = () => {
  const description = `This snapshot is protected from deletion.`;

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: '200px',
          },
        },
      }}
      title={
        <Stack padding='4px' gap='12px'>
          <Typography color='var(--mui-palette-Tooltip-text)'>
            {description}
          </Typography>
        </Stack>
      }
    >
      <span>
        <LockIcon />
      </span>
    </Tooltip>
  );
};
